/**
 * Synthetic Demo Transport Layer
 * Intercepts HTTP requests made by surfaces in DEMO mode and returns contract-accurate responses.
 */
import {
  getDemoProgram,
  getDemoBoardroomOverview,
  getDemoWorkflowStages,
  getDemoWorkTasks,
  getDemoDecisions,
  getDemoRisks,
  getDemoSignals,
  getDemoEvidence,
  getDemoKnowledgeDocuments,
  getDemoAgents,
  getDemoCanvasObjects,
  getDemoCanvasActivity,
  getDemoExecutionGraph,
  getDemoEnterprisePosture,
} from './demoRepository';

export function handleSyntheticDemoRequest(url = '', method = 'get', data = null) {
  const cleanUrl = String(url || '').toLowerCase();
  const upperMethod = String(method || 'get').toUpperCase();

  // 1. Auth Me
  if (cleanUrl.includes('/auth/me')) {
    return {
      status: 200,
      data: {
        id: 'usr-thomas',
        email: 'thomas@talktoplus.io',
        full_name: 'Thomas Nordstrom - Board Director',
        role: 'super_admin',
        title: 'Board Director',
        tenant_id: 'default-tenant',
        demo: true,
      },
    };
  }

  // 2. Program / Enterprise Projects
  if (cleanUrl.includes('/enterprises/projects') || cleanUrl.includes('/programs')) {
    return {
      status: 200,
      data: [getDemoProgram()],
    };
  }

  // 3. Execution & Boardroom Overviews
  if (cleanUrl.includes('/execution/overview') || cleanUrl.includes('/boardroom/overview')) {
    return {
      status: 200,
      data: getDemoBoardroomOverview(),
    };
  }

  // 4. Work: Workflow Stages & Tasks & Actions
  if (cleanUrl.includes('/workspace/stages') || cleanUrl.includes('/workflow/stages') || cleanUrl.includes('/stages')) {
    return {
      status: 200,
      data: getDemoWorkflowStages(),
    };
  }

  if (cleanUrl.includes('/work/tasks') || cleanUrl.includes('/tasks') || cleanUrl.includes('/action-cards') || cleanUrl.includes('/actions')) {
    return {
      status: 200,
      data: getDemoWorkTasks(),
    };
  }

  if (cleanUrl.includes('/decisions') || cleanUrl.includes('/decision-hub')) {
    return {
      status: 200,
      data: getDemoDecisions(),
    };
  }

  // 5. Intelligence: Risks, Signals, Knowledge
  if (cleanUrl.includes('/risks') || cleanUrl.includes('/risk-engine') || cleanUrl.includes('/risk-matrix')) {
    return {
      status: 200,
      data: getDemoRisks(),
    };
  }

  if (cleanUrl.includes('/signals') || cleanUrl.includes('/sensing')) {
    return {
      status: 200,
      data: getDemoSignals(),
    };
  }

  if (cleanUrl.includes('/knowledge') || cleanUrl.includes('/documents') || cleanUrl.includes('/rag')) {
    return {
      status: 200,
      data: getDemoKnowledgeDocuments(),
    };
  }

  // 6. Evidence & Data Surface
  if (cleanUrl.includes('/evidence') || cleanUrl.includes('/sources') || cleanUrl.includes('/data-pipelines')) {
    return {
      status: 200,
      data: getDemoEvidence(),
    };
  }

  // 7. Canvas & Native Workspaces: Workspace, Nodes, Activity, Agents
  if (cleanUrl.includes('/workspace/native')) {
    const stages = getDemoWorkflowStages().map((s, idx) => ({
      id: s.id,
      name: s.name,
      order_index: idx + 1,
      status: s.status,
    }));
    const tasks = getDemoWorkTasks().map((t, idx) => ({
      id: t.id,
      stage_id: 'stage-4',
      name: t.title,
      target_role: 'participant',
      order_index: idx + 1,
      status: t.status === 'COMPLETED' ? 'answered' : 'open',
      priority: t.priority,
    }));
    const demoWorkspace = {
      id: 'ws-demo-northstar',
      name: 'Northstar Transformation Program',
      current_stage_id: 'stage-4',
      stages,
      tasks,
    };
    if (cleanUrl.includes('/responses')) {
      return {
        status: 200,
        data: [],
      };
    }
    if (cleanUrl.endsWith('/workspace/native') || cleanUrl.endsWith('/workspace/native/')) {
      return {
        status: 200,
        data: [demoWorkspace],
      };
    }
    return {
      status: 200,
      data: {
        workspace: demoWorkspace,
        stages,
        tasks,
      },
    };
  }

  if (cleanUrl.includes('/canvas/workspace') || cleanUrl.includes('/canvas/nodes')) {
    return {
      status: 200,
      data: {
        id: 'ws-demo-northstar',
        name: 'Northstar Program Canvas',
        objects: getDemoCanvasObjects(),
        activity: getDemoCanvasActivity(),
        agents: getDemoAgents(),
        read_only: false,
        epistemic_mode: 'DEMO_SIMULATION',
      },
    };
  }

  if (cleanUrl.includes('/canvas/activity') || cleanUrl.includes('/audit')) {
    return {
      status: 200,
      data: getDemoCanvasActivity(),
    };
  }

  if (cleanUrl.includes('/agents')) {
    return {
      status: 200,
      data: getDemoAgents(),
    };
  }

  // 8. Execution Graph / Worktree
  if (cleanUrl.includes('/execution-graph') || cleanUrl.includes('/worktree')) {
    return {
      status: 200,
      data: getDemoExecutionGraph(),
    };
  }

  // 9. Enterprise / Posture / System Health
  if (cleanUrl.includes('/enterprise') || cleanUrl.includes('/posture') || cleanUrl.includes('/system-health') || cleanUrl.includes('/ready')) {
    return {
      status: 200,
      data: getDemoEnterprisePosture(),
    };
  }

  // 10. Chat / Strategy RAG Query / Organizational Intelligence
  if (cleanUrl.includes('/chat') || cleanUrl.includes('/strategy') || cleanUrl.includes('/query')) {
    const questionText = (data?.question || data?.query || data?.prompt || data?.content || '').toLowerCase();
    let answerText = 'Northstar Transformation Program is currently in conditional progression (86% evidence coverage). The primary emerging bottleneck is the critical Delivery Dependency on external vendor SLA.';
    let citations = [
      {
        citation_id: 'C1',
        source_ref: 'demo-evi-delivery-review',
        source_type: 'evidence',
        title: 'Vendor Dependency Mitigation Plan',
        locator: 'vault://reviews/vendor-mitigation-plan.pdf',
        authority_label: 'Steering Committee Approved',
        epistemic_label: 'Observed',
        excerpt: 'Critical external supplier committed to stage-3 delivery timeline under revised SLA.',
      },
      {
        citation_id: 'C2',
        source_ref: 'demo-risk-delivery-dependency',
        source_type: 'risk',
        title: 'Delivery Dependency (Critical / Emerging)',
        locator: 'talkto://risk/dependency',
        authority_label: 'Risk Committee',
        epistemic_label: 'Observed',
        excerpt: 'One critical vendor delivery dependency remains under active mitigation.',
      },
    ];

    if (questionText.includes('block') || questionText.includes('risk') || questionText.includes('dependency')) {
      answerText = 'Critical Delivery Dependency (Severity: 4.8) requires dual-operator signoff on secondary pipeline failover agreement before Stage 4 completion.';
    } else if (questionText.includes('decision') || questionText.includes('board') || questionText.includes('approval') || questionText.includes('stage')) {
      answerText = 'Conditional Progression to Stage 4 is pending review by Board Director Thomas Nordstrom. Conditions require evidence coverage to stay above 85% and SLA mitigation signed.';
      citations = [
        {
          citation_id: 'C1',
          source_ref: 'demo-dec-stage-gate',
          source_type: 'decision',
          title: 'Conditional Progression to Stage 4',
          locator: 'talkto://decisions/stage-gate',
          authority_label: 'Four-Eyes Enforced',
          epistemic_label: 'Observed',
          excerpt: 'Strategic alignment confirmed; delivery dependency contained within threshold.',
        },
        {
          citation_id: 'C2',
          source_ref: 'demo-evi-steering-record',
          source_type: 'evidence',
          title: 'Executive Steering Group Decision Record',
          locator: 'vault://governance/2026-08-steering-record.pdf',
          authority_label: 'Corporate Secretary',
          epistemic_label: 'Observed',
          excerpt: 'Formal steering approval for conditional stage progression subject to vendor mitigation.',
        },
      ];
    }

    return {
      status: 200,
      data: {
        query_id: `q-demo-${Date.now()}`,
        intent: data?.intent || 'FACT',
        query_status: 'SUCCESS',
        truth_state: 'KNOWN',
        answer_text: answerText,
        response: answerText,
        answer: answerText,
        synthesis_status: 'AVAILABLE',
        citations: citations,
        sources: citations,
        claim_support_summary: { supported: citations.length, unsupported: 0 },
        limitations: ['Synthetic demonstration session'],
        bounded: true,
        confidence: 0.96,
        epistemic_type: 'SCENARIO_CITATION',
        generated_at: '2026-08-28T07:30:00Z',
      },
    };
  }

  // Default fallback for any other demo route
  return {
    status: 200,
    data: { ok: true, demo: true, message: 'Synthetic demo operation succeeded.' },
  };
}
